// ──────────────────────────────────────────────────────────────────────────
// Comparison primitives
// ──────────────────────────────────────────────────────────────────────────

export const COMPARISON_TYPES = [
  "EQUALS",
  "NOT_EQUALS",
  "CONTAINS",
  "NOT_CONTAINS",
  "LESS_THAN",
  "LESS_THAN_EQUALS",
  "GREATER_THAN",
  "GREATER_THAN_EQUALS",
  "BEGINS_WITH",
  "ENDS_WITH"
] as const

export type ComparisonType = (typeof COMPARISON_TYPES)[number]

/** Comparisons that compare against a single value. */
export const SINGLE_VALUE_COMPARISONS: ReadonlySet<ComparisonType> = new Set([
  "EQUALS",
  "NOT_EQUALS",
  "LESS_THAN",
  "LESS_THAN_EQUALS",
  "GREATER_THAN",
  "GREATER_THAN_EQUALS",
  "BEGINS_WITH",
  "ENDS_WITH"
])

/** Comparisons that accept >=1 value (CONTAINS, NOT_CONTAINS). */
export const MULTI_VALUE_COMPARISONS: ReadonlySet<ComparisonType> = new Set([
  "CONTAINS",
  "NOT_CONTAINS"
])

/** Backend-enforced cap on `values` array length for ComparisonString /
 *  ComparisonInteger / OrderDate / key-value lists. */
export const MAX_COMPARISON_VALUES = 100

export interface ComparisonString {
  comparison: ComparisonType
  values: string[]
}

export interface ComparisonInteger {
  comparison: ComparisonType
  values: number[]
}

export interface ConditionCheckArrayCount {
  length: ComparisonInteger
}

// ──────────────────────────────────────────────────────────────────────────
// Check-type discriminator
// ──────────────────────────────────────────────────────────────────────────

export const CHECK_TYPES = [
  "AND",
  "OR",
  "PAYMENT_PROVIDER",
  "SHIPPING_PROVIDER",
  "PROMOTION_PROVIDER",
  "VOUCHER_PROVIDER",
  "GIFT_CARD_PROVIDER",
  "GIFT_CARD_PRODUCT_PROVIDER",
  "BONUS_PROVIDER",
  "TAX_PROVIDER",
  "EXTERNAL_PRICE_PROVIDER",
  "ORDER_LINE",
  "ORDER_LINES",
  "SHIPPING_FEES",
  "GIFTS",
  "ORDER_TOTALS",
  "ORDER_DATE",
  "CUSTOM_HTTP",
  "ADDRESSES",
  "DISCOUNT_CODE_RULE"
] as const

export type CheckType = (typeof CHECK_TYPES)[number]

const CHECK_TYPE_SET: ReadonlySet<string> = new Set<string>(CHECK_TYPES)

export const isCheckType = (value: string | undefined | null): value is CheckType =>
  typeof value === "string" && CHECK_TYPE_SET.has(value)

// ──────────────────────────────────────────────────────────────────────────
// Compound conditions
// ──────────────────────────────────────────────────────────────────────────

export interface NodeConditionCheckAnd {
  type: "AND"
  check1: NodeConditionCheck
  check2: NodeConditionCheck
}

export interface NodeConditionCheckOr {
  type: "OR"
  check1: NodeConditionCheck
  check2: NodeConditionCheck
}

// ──────────────────────────────────────────────────────────────────────────
// Provider field-bag conditions
// ──────────────────────────────────────────────────────────────────────────

export interface NodeConditionCheckPaymentProviderFields {
  id?: ComparisonString
  name?: ComparisonString
  status?: ComparisonString
  method?: ComparisonString
}

export interface NodeConditionCheckPaymentProvider {
  type: "PAYMENT_PROVIDER"
  fields: NodeConditionCheckPaymentProviderFields
}

export interface NodeConditionCheckShippingProviderFields {
  id?: ComparisonString
  name?: ComparisonString
}

export interface NodeConditionCheckShippingProvider {
  type: "SHIPPING_PROVIDER"
  fields: NodeConditionCheckShippingProviderFields
}

export interface NodeConditionCheckPromotionProviderFields {
  id?: ComparisonString
  name?: ComparisonString
}

export interface NodeConditionCheckPromotionProvider {
  type: "PROMOTION_PROVIDER"
  fields: NodeConditionCheckPromotionProviderFields
}

export interface NodeConditionCheckVoucherProviderFields {
  id?: ComparisonString
  name?: ComparisonString
}

export interface NodeConditionCheckVoucherProvider {
  type: "VOUCHER_PROVIDER"
  fields: NodeConditionCheckVoucherProviderFields
}

export interface NodeConditionCheckGiftCardProviderFields {
  id?: ComparisonString
  name?: ComparisonString
  status?: ComparisonString
  giftCards?: ConditionCheckArrayCount
}

export interface NodeConditionCheckGiftCardProvider {
  type: "GIFT_CARD_PROVIDER"
  fields: NodeConditionCheckGiftCardProviderFields
}

export interface NodeConditionCheckGiftCardProductProviderFields {
  id?: ComparisonString
  name?: ComparisonString
  status?: ComparisonString
  giftCardProducts?: ConditionCheckArrayCount
}

export interface NodeConditionCheckGiftCardProductProvider {
  type: "GIFT_CARD_PRODUCT_PROVIDER"
  fields: NodeConditionCheckGiftCardProductProviderFields
}

export interface NodeConditionCheckBonusProviderFields {
  id?: ComparisonString
  name?: ComparisonString
  status?: ComparisonString
  customerId?: ComparisonString
  amount?: ComparisonInteger
  points?: ComparisonInteger
}

export interface NodeConditionCheckBonusProvider {
  type: "BONUS_PROVIDER"
  fields: NodeConditionCheckBonusProviderFields
}

export interface NodeConditionCheckTaxProviderFields {
  id?: ComparisonString
  name?: ComparisonString
}

export interface NodeConditionCheckTaxProvider {
  type: "TAX_PROVIDER"
  fields: NodeConditionCheckTaxProviderFields
}

export interface NodeConditionCheckExternalPriceProviderFields {
  id?: ComparisonString
  name?: ComparisonString
}

export interface NodeConditionCheckExternalPriceProvider {
  type: "EXTERNAL_PRICE_PROVIDER"
  fields: NodeConditionCheckExternalPriceProviderFields
}

// ──────────────────────────────────────────────────────────────────────────
// OrderLine — nested field bags (campaign plus key-value lists for the
// customAttributes, tags, and options fields).
// ──────────────────────────────────────────────────────────────────────────

export interface NodeConditionCheckOrderLineCampaignFields {
  campaignId?: ComparisonString
  name?: ComparisonString
  campaignGroupId?: ComparisonString
}

export interface NodeConditionCheckOrderLineFieldsCampaign {
  fields: NodeConditionCheckOrderLineCampaignFields
}

export interface NodeConditionCheckOrderLineKeyValuePair {
  key?: ComparisonString
  value?: ComparisonString
}

export interface NodeConditionCheckOrderLineFieldsKeyValues {
  keyValues: NodeConditionCheckOrderLineKeyValuePair[]
}

export interface NodeConditionCheckOrderLineFields {
  productParentId?: ComparisonString
  productVariantId?: ComparisonString
  campaign?: NodeConditionCheckOrderLineFieldsCampaign
  customAttributes?: NodeConditionCheckOrderLineFieldsKeyValues
  tags?: NodeConditionCheckOrderLineFieldsKeyValues
  options?: NodeConditionCheckOrderLineFieldsKeyValues
}

export interface NodeConditionCheckOrderLine {
  type: "ORDER_LINE"
  fields: NodeConditionCheckOrderLineFields
}

// ──────────────────────────────────────────────────────────────────────────
// Array-count conditions (length only)
// ──────────────────────────────────────────────────────────────────────────

export interface NodeConditionCheckOrderLines {
  type: "ORDER_LINES"
  count: ConditionCheckArrayCount
}

export interface NodeConditionCheckShippingFees {
  type: "SHIPPING_FEES"
  count: ConditionCheckArrayCount
}

export interface NodeConditionCheckGifts {
  type: "GIFTS"
  count: ConditionCheckArrayCount
}

// ──────────────────────────────────────────────────────────────────────────
// OrderTotals
// ──────────────────────────────────────────────────────────────────────────

export interface NodeConditionCheckOrderTotalsFields {
  subTotal?: ComparisonInteger
  taxTotal?: ComparisonInteger
  discountTotal?: ComparisonInteger
  shippingTotal?: ComparisonInteger
  bonusTotal?: ComparisonInteger
  giftCardTotal?: ComparisonInteger
  grandTotal?: ComparisonInteger
}

export interface NodeConditionCheckOrderTotals {
  type: "ORDER_TOTALS"
  fields: NodeConditionCheckOrderTotalsFields
}

// ──────────────────────────────────────────────────────────────────────────
// OrderDate
// ──────────────────────────────────────────────────────────────────────────

export interface NodeConditionCheckOrderDate {
  type: "ORDER_DATE"
  comparison: ComparisonType
  /** RFC-3339 datetime strings. */
  values: string[]
}

// ──────────────────────────────────────────────────────────────────────────
// CustomHTTP
// ──────────────────────────────────────────────────────────────────────────

export interface NodeConditionCheckCustomHTTP {
  type: "CUSTOM_HTTP"
  endpoint: string
  /** Optional arbitrary JSON value forwarded verbatim to the endpoint
   *  (deserialised on the backend as `json.RawMessage`, max 500 chars
   *  serialised). The editor lets the user type it as JSON text and
   *  parses it on save. */
  customData?: unknown
}

// ──────────────────────────────────────────────────────────────────────────
// Addresses
// ──────────────────────────────────────────────────────────────────────────

export interface NodeConditionCheckAddressFields {
  country?: ComparisonString
  pcc?: ComparisonString
  telephoneNumber?: ComparisonString
  stateOrProvince?: ComparisonString
  streetAddress?: ComparisonString
  city?: ComparisonString
  givenName?: ComparisonString
  familyName?: ComparisonString
  postalCode?: ComparisonString
  streetAddress2?: ComparisonString
  email?: ComparisonString
}

export interface NodeConditionCheckAddressesFields {
  shippingAddress?: NodeConditionCheckAddressFields
  billingAddress?: NodeConditionCheckAddressFields
}

export interface NodeConditionCheckAddresses {
  type: "ADDRESSES"
  fields: NodeConditionCheckAddressesFields
}

// ──────────────────────────────────────────────────────────────────────────
// DiscountCodeRule
// ──────────────────────────────────────────────────────────────────────────

export interface NodeConditionCheckDiscountCodeRuleFields {
  id?: ComparisonString
  code?: ComparisonString
}

export interface NodeConditionCheckDiscountCodeRule {
  type: "DISCOUNT_CODE_RULE"
  fields: NodeConditionCheckDiscountCodeRuleFields
}

// ──────────────────────────────────────────────────────────────────────────
// Discriminated union over all 20 check types
// ──────────────────────────────────────────────────────────────────────────

export type NodeConditionCheck =
  | NodeConditionCheckAnd
  | NodeConditionCheckOr
  | NodeConditionCheckPaymentProvider
  | NodeConditionCheckShippingProvider
  | NodeConditionCheckPromotionProvider
  | NodeConditionCheckVoucherProvider
  | NodeConditionCheckGiftCardProvider
  | NodeConditionCheckGiftCardProductProvider
  | NodeConditionCheckBonusProvider
  | NodeConditionCheckTaxProvider
  | NodeConditionCheckExternalPriceProvider
  | NodeConditionCheckOrderLine
  | NodeConditionCheckOrderLines
  | NodeConditionCheckShippingFees
  | NodeConditionCheckGifts
  | NodeConditionCheckOrderTotals
  | NodeConditionCheckOrderDate
  | NodeConditionCheckCustomHTTP
  | NodeConditionCheckAddresses
  | NodeConditionCheckDiscountCodeRule

// ──────────────────────────────────────────────────────────────────────────
// Workflow node + action shapes (the JSON envelope around checks)
// ──────────────────────────────────────────────────────────────────────────

export interface WorkflowActionDoReason {
  code: string
  description: string
}

export interface WorkflowActionDoSelection {
  giftCardProducts?: boolean
}

export interface WorkflowActionDo {
  type: string
  reason?: WorkflowActionDoReason
  customState?: string
  selection?: WorkflowActionDoSelection
}

export interface WorkflowApiNode {
  type: "CONDITION" | "BRANCH" | "ACTION"
  name: string
  then?: WorkflowApiNode[]
  otherwise?: WorkflowApiNode[]
  check?: NodeConditionCheck
  do?: WorkflowActionDo
  branches?: WorkflowApiBranchCondition[]
}

export interface WorkflowApiBranchCondition {
  type: "CONDITION"
  name: string
  check: NodeConditionCheck
  then?: WorkflowApiNode[]
}

// ──────────────────────────────────────────────────────────────────────────
// Defensive parser
// ──────────────────────────────────────────────────────────────────────────

/** Coerce an unknown value (typed object, JSON string, or null) into a
 *  NodeConditionCheck. Returns undefined if the input doesn't carry a
 *  recognised `type` discriminator. Used by the workflow + execution
 *  loaders, which receive `check` as either a parsed object or a raw JSON
 *  string depending on the GraphQL field. */
export const coerceCheck = (raw: unknown): NodeConditionCheck | undefined => {
  if (raw == null) return undefined
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as NodeConditionCheck
    } catch {
      return undefined
    }
  }
  if (typeof raw === "object") {
    const t = (raw as { type?: unknown }).type
    if (typeof t === "string" && (isCheckType(t) || t === "AND" || t === "OR")) {
      return raw as NodeConditionCheck
    }
  }
  return undefined
}
